import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Separator } from "../ui/separator";
import { ScrollArea } from "../ui/scroll-area";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "../ui/select";
import { MapleGameHost } from "@maple/engine-react";
import type { MapleEngine } from "@maple/engine-core";
import { Activity, Monitor, Cpu, Gauge, Play, Maximize2, Minimize2, Box } from "lucide-react";
import { useToast } from "../ui/use-toast";
import { Switch } from "../ui/switch";
import { Slider } from "../ui/slider";

// ADDED: Three.js for real 3D rendering
import {
  Scene,
  PerspectiveCamera,
  WebGLRenderer,
  Color,
  AmbientLight,
  DirectionalLight,
  Mesh,
  MeshStandardMaterial,
  MeshPhysicalMaterial,
  BoxGeometry,
  CapsuleGeometry,
  GridHelper,
  PlaneGeometry,
  Vector3,
  PMREMGenerator,
  SphereGeometry,
  TorusKnotGeometry,
  Texture,
  MeshBasicMaterial,
  BackSide,
  EquirectangularReflectionMapping,
} from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { EXRLoader } from "three/examples/jsm/loaders/EXRLoader.js";
// ADD: TrackballControls for PBR demo
import { TrackballControls } from "three/examples/jsm/controls/TrackballControls.js";

type SampleId =
  | "moving-dot"
  | "bouncing-balls"
  | "spinning-cube"
  | "shaded-icosahedron"
  | "shooter-3d"
  | "creative-3d"
  | "webgl-3d-lab"
  | "pbr-environment";

const samples = [
  { id: "moving-dot" as SampleId, label: "Moving Dot (WASD/Arrows)" },
  { id: "bouncing-balls" as SampleId, label: "Bouncing Balls" },
  { id: "spinning-cube" as SampleId, label: "Spinning 3D Cube (2D projection)" },
  { id: "shaded-icosahedron" as SampleId, label: "Shaded Icosahedron (Lambert shading)" },
  { id: "shooter-3d" as SampleId, label: "3D Shooter Demo" },
  { id: "creative-3d" as SampleId, label: "Creative 3D Ribbons" },
  { id: "webgl-3d-lab" as SampleId, label: "3D Lab (WebGL)" },
  { id: "pbr-environment" as SampleId, label: "PBR Environment (HDRI)" },
];

export const MapleEngineApp: React.FC = () => {
  const [selected, setSelected] = useState<SampleId>("moving-dot");
  const [targetFPS] = useState<number>(60);
  const [fps, setFps] = useState<number>(0);
  const [canvasPx, setCanvasPx] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  const [cssSize, setCssSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  const dpr = useMemo(() => Math.max(1, window.devicePixelRatio || 1), []);
  const { toast } = useToast();

  const viewportRef = useRef<HTMLDivElement | null>(null);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);

  // ADDED: PBR env UI state + refs (keeps engine stable while sliders change)
  const [envPreset, setEnvPreset] = useState<"cliff"|"room"|"parking"|"country">("cliff");
  const [envAutoRotate, setEnvAutoRotate] = useState<boolean>(true);
  const [envRotationDeg, setEnvRotationDeg] = useState<number>(0); // 0..360
  const [groundHeight, setGroundHeight] = useState<number>(1.85);
  const [groundRadius, setGroundRadius] = useState<number>(6.36);
  // NEW: toggle interactivity for the PBR viewport
  const [envInteractive, setEnvInteractive] = useState<boolean>(false);

  const envPresetRef = useRef(envPreset);
  const envAutoRotateRef = useRef(envAutoRotate);
  const envRotationRef = useRef(envRotationDeg * Math.PI / 180);
  const groundHeightRef = useRef(groundHeight);
  const groundRadiusRef = useRef(groundRadius);
  // NEW: ref for interactivity
  const envInteractiveRef = useRef(envInteractive);

  useEffect(() => { envPresetRef.current = envPreset; }, [envPreset]);
  useEffect(() => { envAutoRotateRef.current = envAutoRotate; }, [envAutoRotate]);
  useEffect(() => { envRotationRef.current = envRotationDeg * Math.PI / 180; }, [envRotationDeg]);
  useEffect(() => { groundHeightRef.current = groundHeight; }, [groundHeight]);
  useEffect(() => { groundRadiusRef.current = groundRadius; }, [groundRadius]);
  // NEW: keep interactivity ref in sync
  useEffect(() => { envInteractiveRef.current = envInteractive; }, [envInteractive]);

  useEffect(() => {
    const onFsChange = () => {
      setIsFullscreen(document.fullscreenElement === viewportRef.current);
    };
    document.addEventListener("fullscreenchange", onFsChange);
    return () => {
      document.removeEventListener("fullscreenchange", onFsChange);
    };
  }, []);

  const toggleFullscreen = useCallback(() => {
    const el = viewportRef.current;
    if (!el) return;
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      el.requestFullscreen?.();
    }
  }, []);

  const updateCanvasStats = (engine?: MapleEngine) => {
    if (!engine) return;
    const canvas = engine.getCanvas();
    const rect = canvas.getBoundingClientRect();
    setCanvasPx({ w: canvas.width, h: canvas.height });
    setCssSize({ w: Math.round(rect.width), h: Math.round(rect.height) });
  };

  const createGame = useCallback((engine: MapleEngine) => {
    updateCanvasStats(engine);

    let frameCount = 0;
    let last = performance.now();

    const statsSystem = {
      update() {
        frameCount++;
        const now = performance.now();
        if (now - last >= 500) {
          const measuredFps = Math.round((frameCount * 1000) / (now - last));
          setFps(measuredFps);
          frameCount = 0;
          last = now;
          updateCanvasStats(engine);
        }
      },
    };

    const dotSample = () => {
      const canvas = engine.getCanvas();
      const ctx = engine.getContext2D();
      const radius = Math.max(8, Math.floor(10 * dpr));
      let x = canvas.width / 2;
      let y = canvas.height / 2;
      const speed = 220; // px/sec

      const moveSystem = {
        update(dt: number) {
          let dx = 0;
          let dy = 0;
          const input = engine.input;
          if (input.isKeyDown("ArrowUp") || input.isKeyDown("KeyW")) dy -= 1;
          if (input.isKeyDown("ArrowDown") || input.isKeyDown("KeyS")) dy += 1;
          if (input.isKeyDown("ArrowLeft") || input.isKeyDown("KeyA")) dx -= 1;
          if (input.isKeyDown("ArrowRight") || input.isKeyDown("KeyD")) dx += 1;
          const mag = Math.hypot(dx, dy);
          if (mag > 0) {
            dx /= mag;
            dy /= mag;
            x += dx * speed * dt;
            y += dy * speed * dt;
          }
          x = Math.min(canvas.width - radius, Math.max(radius, x));
          y = Math.min(canvas.height - radius, Math.max(radius, y));
        },
      };

      const renderSystem = {
        update() {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.save();
          ctx.strokeStyle = "rgba(148, 163, 184, 0.25)";
          const step = 20 * dpr;
          for (let gx = 0; gx < canvas.width; gx += step) {
            ctx.beginPath();
            ctx.moveTo(gx, 0);
            ctx.lineTo(gx, canvas.height);
            ctx.stroke();
          }
          for (let gy = 0; gy < canvas.height; gy += step) {
            ctx.beginPath();
            ctx.moveTo(0, gy);
            ctx.lineTo(canvas.width, gy);
            ctx.stroke();
          }
          ctx.restore();

          ctx.save();
          ctx.fillStyle = "#ef4444";
          ctx.beginPath();
          ctx.arc(x, y, radius, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
        },
      };

      engine.addSystem(moveSystem);
      engine.addSystem(renderSystem);
    };

    const bouncingBallsSample = () => {
      const canvas = engine.getCanvas();
      const ctx = engine.getContext2D();
      const rng = (min: number, max: number) => Math.random() * (max - min) + min;
      const count = 18;
      const balls = Array.from({ length: count }).map(() => {
        const r = rng(6, 16) * dpr;
        return {
          x: rng(r, canvas.width - r),
          y: rng(r, canvas.height - r),
          vx: rng(-140, 140),
          vy: rng(-140, 140),
          r,
          color: `hsl(${Math.floor(rng(0, 360))}, 80%, 60%)`,
        };
      });

      const physicsSystem = {
        update(dt: number) {
          for (const b of balls) {
            b.x += b.vx * dt;
            b.y += b.vy * dt;
            if (b.x < b.r) { b.x = b.r; b.vx *= -1; }
            if (b.x > canvas.width - b.r) { b.x = canvas.width - b.r; b.vx *= -1; }
            if (b.y < b.r) { b.y = b.r; b.vy *= -1; }
            if (b.y > canvas.height - b.r) { b.y = canvas.height - b.r; b.vy *= -1; }
          }
        },
      };

      const renderSystem = {
        update() {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.save();
          ctx.fillStyle = "rgba(2,6,23,1)";
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.restore();
          for (const b of balls) {
            ctx.save();
            ctx.fillStyle = b.color;
            ctx.beginPath();
            ctx.arc(b.x, b.y, b.r, 0, Math.PI * 2);
            ctx.fill();
            ctx.restore();
          }
        },
      };

      engine.addSystem(physicsSystem);
      engine.addSystem(renderSystem);
    };

    const spinningCubeSample = () => {
      const canvas = engine.getCanvas();
      const ctx = engine.getContext2D();
      let rx = 0; // rotation around X
      let ry = 0; // rotation around Y
      const speed = Math.PI / 3; // rad/sec
      const size = Math.min(canvas.width, canvas.height) * 0.25;
      const center = { x: canvas.width / 2, y: canvas.height / 2 };
      const d = 3; // camera distance factor for perspective

      // Cube vertices in model space
      const verts = [
        [-1, -1, -1], [1, -1, -1], [1, 1, -1], [-1, 1, -1],
        [-1, -1,  1], [1, -1,  1], [1, 1,  1], [-1, 1,  1],
      ];
      const edges = [
        [0,1],[1,2],[2,3],[3,0], // back face
        [4,5],[5,6],[6,7],[7,4], // front face
        [0,4],[1,5],[2,6],[3,7], // connections
      ];

      const project = (x: number, y: number, z: number) => {
        // rotate
        const cosx = Math.cos(rx), sinx = Math.sin(rx);
        const cosy = Math.cos(ry), siny = Math.sin(ry);
        // Y rotation
        const x1 = x * cosy + z * siny;
        const z1 = -x * siny + z * cosy;
        // X rotation
        const y2 = y * cosx - z1 * sinx;
        const z2 = y * sinx + z1 * cosx;
        // perspective
        const f = d / (d + z2);
        const sx = center.x + (x1 * f) * size;
        const sy = center.y + (y2 * f) * size;
        return [sx, sy];
      };

      const updateSystem = {
        update(dt: number) {
          rx += speed * dt;
          ry += (speed * 0.8) * dt;
        },
      };

      const renderSystem = {
        update() {
          ctx.clearRect(0, 0, canvas.width, canvas.height);

          // background
          ctx.save();
          ctx.fillStyle = "rgba(2,6,23,1)"; // slate-950
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.restore();

          // draw cube edges
          ctx.save();
          ctx.strokeStyle = "#38bdf8"; // sky-400
          ctx.lineWidth = Math.max(1, Math.floor(dpr));
          for (const [a, b] of edges) {
            const [ax, ay] = project(verts[a][0], verts[a][1], verts[a][2]);
            const [bx, by] = project(verts[b][0], verts[b][1], verts[b][2]);
            ctx.beginPath();
            ctx.moveTo(ax, ay);
            ctx.lineTo(bx, by);
            ctx.stroke();
          }
          ctx.restore();

          // label
          ctx.save();
          ctx.fillStyle = "rgba(255,255,255,0.9)";
          ctx.font = `${12 * dpr}px system-ui, -apple-system, Segoe UI, Roboto`;
          ctx.fillText("Spinning 3D Cube (2D projection)", 12 * dpr, 20 * dpr);
          ctx.restore();
        },
      };

      engine.addSystem(updateSystem);
      engine.addSystem(renderSystem);
    };

    // ADDED: Shaded Icosahedron sample with simple Lambert shading
    const shadedIcosahedronSample = () => {
      const canvas = engine.getCanvas();
      const ctx = engine.getContext2D();

      let rx = 0;
      let ry = 0;
      const speed = Math.PI / 4; // rad/sec
      const size = Math.min(canvas.width, canvas.height) * 0.28; // model scale
      const center = { x: canvas.width / 2, y: canvas.height / 2 };
      const d = 3; // camera distance factor for perspective

      // Icosahedron vertices (classic)
      const phi = (1 + Math.sqrt(5)) / 2;
      const s = 1 / phi; // normalize to roughly [-1,1]
      const verts = [
        [-1,  phi,  0], [ 1,  phi,  0], [-1, -phi,  0], [ 1, -phi,  0],
        [ 0, -1,  phi], [ 0,  1,  phi], [ 0, -1, -phi], [ 0,  1, -phi],
        [ phi,  0, -1], [ phi,  0,  1], [-phi,  0, -1], [-phi,  0,  1],
      ].map(([x, y, z]) => [x * s, y * s, z * s]);

      // 20 triangular faces (indices)
      const faces = [
        [0,11,5],[0,5,1],[0,1,7],[0,7,10],[0,10,11],
        [1,5,9],[5,11,4],[11,10,2],[10,7,6],[7,1,8],
        [3,9,4],[3,4,2],[3,2,6],[3,6,8],[3,8,9],
        [4,9,5],[2,4,11],[6,2,10],[8,6,7],[9,8,1],
      ] as const;

      // Light direction (normalized)
      const L = (() => {
        const lx = 0.5, ly = 0.8, lz = -0.6;
        const lm = Math.hypot(lx, ly, lz);
        return [lx / lm, ly / lm, lz / lm] as const;
      })();

      const rotate = (x: number, y: number, z: number) => {
        const cosx = Math.cos(rx), sinx = Math.sin(rx);
        const cosy = Math.cos(ry), siny = Math.sin(ry);
        // Y rotation
        const x1 = x * cosy + z * siny;
        const z1 = -x * siny + z * cosy;
        // X rotation
        const y2 = y * cosx - z1 * sinx;
        const z2 = y * sinx + z1 * cosx;
        return [x1, y2, z2] as const;
      };

      const project = (x: number, y: number, z: number) => {
        const f = d / (d + z);
        const sx = center.x + (x * f) * size;
        const sy = center.y + (y * f) * size;
        return [sx, sy, z] as const; // include z for sorting
      };

      const updateSystem = {
        update(dt: number) {
          rx += speed * dt;
          ry += (speed * 0.7) * dt;
        },
      };

      const renderSystem = {
        update() {
          ctx.clearRect(0, 0, canvas.width, canvas.height);

          // background
          ctx.save();
          ctx.fillStyle = "rgba(2,6,23,1)"; // slate-950
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.restore();

          // Rotate and project all vertices
          const rotated = verts.map(([x, y, z]) => rotate(x, y, z));
          const projected = rotated.map(([x, y, z]) => project(x, y, z));

          // Build drawable faces with shading and depth
          const drawable = faces.map(([a, b, c]) => {
            const va = rotated[a], vb = rotated[b], vc = rotated[c];

            // Compute face normal in camera space
            const ux = vb[0] - va[0], uy = vb[1] - va[1], uz = vb[2] - va[2];
            const vx = vc[0] - va[0], vy = vc[1] - va[1], vz = vc[2] - va[2];
            let nx = uy * vz - uz * vy;
            let ny = uz * vx - ux * vz;
            let nz = ux * vy - uy * vx;
            const nm = Math.hypot(nx, ny, nz) || 1;
            nx /= nm; ny /= nm; nz /= nm;

            // Backface culling relative to camera (looking down -Z)
            const facing = nz < 0;

            // Lambert shading
            const lambert = Math.max(0, nx * L[0] + ny * L[1] + nz * L[2]);
            const ambient = 0.15;
            const intensity = Math.min(1, ambient + lambert * 0.85);

            // Depth for painter's algorithm (average z)
            const depth = (projected[a][2] + projected[b][2] + projected[c][2]) / 3;

            return {
              a, b, c,
              depth,
              facing,
              intensity,
            };
          })
          // Sort: draw far faces first (larger positive z are farther)
          .sort((f1, f2) => f1.depth - f2.depth);

          // Draw faces
          for (const f of drawable) {
            if (!f.facing) continue; // cull backfaces

            const [ax, ay] = projected[f.a];
            const [bx, by] = projected[f.b];
            const [cx, cy] = projected[f.c];

            // shaded fill color (cool blue palette)
            const H = 200; // hue
            const S = 70;  // saturation
            const L = Math.round(30 + f.intensity * 45); // lightness
            ctx.fillStyle = `hsl(${H} ${S}% ${L}%)`;

            ctx.beginPath();
            ctx.moveTo(ax, ay);
            ctx.lineTo(bx, by);
            ctx.lineTo(cx, cy);
            ctx.closePath();
            ctx.fill();

            // subtle edge stroke
            ctx.save();
            ctx.strokeStyle = "rgba(148,163,184,0.5)"; // slate-400/50
            ctx.lineWidth = Math.max(1, Math.floor(dpr));
            ctx.stroke();
            ctx.restore();
          }

          // label
          ctx.save();
          ctx.fillStyle = "rgba(255,255,255,0.9)";
          ctx.font = `${12 * dpr}px system-ui, -apple-system, Segoe UI, Roboto`;
          ctx.fillText("Shaded Icosahedron (Lambert)", 12 * dpr, 20 * dpr);
          ctx.restore();
        },
      };

      engine.addSystem(updateSystem);
      engine.addSystem(renderSystem);
    };

    // NEW: 3D Shooter Demo (keyboard: arrows/WASD to aim, Space to fire)
    const shooter3DSample = () => {
      const canvas = engine.getCanvas();
      const ctx = engine.getContext2D();
      const size = Math.min(canvas.width, canvas.height) * 0.12;
      const center = { x: canvas.width / 2, y: canvas.height / 2 };
      const d = 4; // camera distance factor

      let yaw = 0;         // aim left/right
      let pitch = 0;       // aim up/down
      const yawSpeed = Math.PI;   // rad/sec
      const pitchSpeed = Math.PI; // rad/sec
      const bulletSpeed = 12;     // world units/sec
      let fireCooldown = 0;
      const fireInterval = 0.12;  // seconds
      let score = 0;
      let lives = 3;
      let spawnTimer = 0;

      type Bullet = { x: number; y: number; z: number; vx: number; vy: number; vz: number; life: number; };
      type Enemy = { x: number; y: number; z: number; r: number; vz: number; };

      const bullets: Bullet[] = [];
      const enemies: Enemy[] = [];

      const rng = (min: number, max: number) => Math.random() * (max - min) + min;

      const spawnEnemy = () => {
        enemies.push({
          x: rng(-3, 3),
          y: rng(-2, 2),
          z: -20 - rng(0, 10),
          r: rng(0.25, 0.45),
          vz: rng(2.0, 3.2),
        });
      };

      const rotateYawPitch = (x: number, y: number, z: number) => {
        // yaw around Y
        const cosy = Math.cos(yaw), siny = Math.sin(yaw);
        const x1 = x * cosy + z * siny;
        const z1 = -x * siny + z * cosy;
        // pitch around X
        const cosp = Math.cos(pitch), sinp = Math.sin(pitch);
        const y2 = y * cosp - z1 * sinp;
        const z2 = y * sinp + z1 * cosp;
        return [x1, y2, z2] as const;
      };

      const project = (x: number, y: number, z: number) => {
        const [rx, ry, rz] = rotateYawPitch(x, y, z);
        const f = d / (d + rz);
        const sx = center.x + (rx * f) * size;
        const sy = center.y + (ry * f) * size;
        return [sx, sy, rz, f] as const;
      };

      const inputSystem = {
        update(dt: number) {
          const input = engine.input;
          const left = input.isKeyDown("ArrowLeft") || input.isKeyDown("KeyA");
          const right = input.isKeyDown("ArrowRight") || input.isKeyDown("KeyD");
          const up = input.isKeyDown("ArrowUp") || input.isKeyDown("KeyW");
          const down = input.isKeyDown("ArrowDown") || input.isKeyDown("KeyS");

          if (left) yaw -= yawSpeed * dt;
          if (right) yaw += yawSpeed * dt;
          if (up) pitch = Math.min(pitch + pitchSpeed * dt, Math.PI / 3);
          if (down) pitch = Math.max(pitch - pitchSpeed * dt, -Math.PI / 3);

          fireCooldown = Math.max(0, fireCooldown - dt);
          if ((input.isKeyDown("Space") || input.isKeyDown("Numpad0")) && fireCooldown <= 0) {
            // forward vector from yaw/pitch
            const dirX = -Math.sin(yaw) * Math.cos(pitch);
            const dirY = Math.sin(pitch);
            const dirZ = -Math.cos(yaw) * Math.cos(pitch);

            bullets.push({
              x: 0, y: 0, z: 0,
              vx: dirX * bulletSpeed,
              vy: dirY * bulletSpeed,
              vz: dirZ * bulletSpeed,
              life: 2.5, // seconds
            });
            fireCooldown = fireInterval;
          }
        },
      };

      const enemySystem = {
        update(dt: number) {
          spawnTimer -= dt;
          if (spawnTimer <= 0) {
            spawnEnemy();
            spawnTimer = rng(0.2, 0.6);
          }
          for (const e of enemies) {
            e.z += e.vz * dt; // move toward camera (z -> 0)
          }
          // remove enemies that passed camera
          for (let i = enemies.length - 1; i >= 0; i--) {
            if (enemies[i].z > 1.5) enemies.splice(i, 1);
          }
        },
      };

      const bulletSystem = {
        update(dt: number) {
          for (const b of bullets) {
            b.x += b.vx * dt;
            b.y += b.vy * dt;
            b.z += b.vz * dt;
            b.life -= dt;
          }
          // cull bullets
          for (let i = bullets.length - 1; i >= 0; i--) {
            const b = bullets[i];
            if (b.life <= 0 || b.z < -40 || Math.abs(b.x) > 40 || Math.abs(b.y) > 40) {
              bullets.splice(i, 1);
            }
          }
          // collisions
          for (let ei = enemies.length - 1; ei >= 0; ei--) {
            const e = enemies[ei];
            let hit = false;
            for (let bi = bullets.length - 1; bi >= 0; bi--) {
              const b = bullets[bi];
              const dx = e.x - b.x, dy = e.y - b.y, dz = e.z - b.z;
              if (Math.hypot(dx, dy, dz) <= e.r) {
                bullets.splice(bi, 1);
                hit = true;
                score += 10;
                break;
              }
            }
            if (hit) enemies.splice(ei, 1);
          }
          // player collision (camera at origin)
          for (let i = enemies.length - 1; i >= 0; i--) {
            const e = enemies[i];
            if (e.z >= 0 && Math.hypot(e.x, e.y) <= e.r * 0.75) {
              enemies.splice(i, 1);
              lives -= 1;
              if (lives <= 0) {
                // reset game state
                bullets.length = 0;
                enemies.length = 0;
                yaw = 0;
                pitch = 0;
                score = 0;
                lives = 3;
              }
            }
          }
        },
      };

      const renderSystem = {
        update() {
          ctx.clearRect(0, 0, canvas.width, canvas.height);

          // background
          ctx.save();
          ctx.fillStyle = "rgba(2,6,23,1)";
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.restore();

          // horizon grid for depth impression
          ctx.save();
          ctx.strokeStyle = "rgba(148, 163, 184, 0.15)";
          const step = 0.5;
          for (let z = -12; z <= -1; z += step) {
            const [ax, ay] = project(-3, -1.8, z);
            const [bx, by] = project(3, -1.8, z);
            ctx.beginPath();
            ctx.moveTo(ax, ay);
            ctx.lineTo(bx, by);
            ctx.stroke();
          }
          ctx.restore();

          // draw enemies (sorted by depth)
          const drawList: { sx: number; sy: number; rpx: number; depth: number; }[] = [];
          for (const e of enemies) {
            const [sx, sy, rz, f] = project(e.x, e.y, e.z);
            drawList.push({ sx, sy, rpx: Math.max(1, e.r * size * f), depth: rz });
          }
          drawList.sort((a, b) => a.depth - b.depth);
          for (const d0 of drawList) {
            ctx.save();
            ctx.fillStyle = "#ef4444";
            ctx.beginPath();
            ctx.arc(d0.sx, d0.sy, d0.rpx, 0, Math.PI * 2);
            ctx.fill();
            ctx.restore();
          }

          // bullets
          for (const b of bullets) {
            const [sx, sy, , f] = project(b.x, b.y, b.z);
            ctx.save();
            ctx.fillStyle = "#e2e8f0";
            ctx.beginPath();
            ctx.arc(sx, sy, Math.max(1, 2 * f), 0, Math.PI * 2);
            ctx.fill();
            ctx.restore();
          }

          // player reticle
          ctx.save();
          ctx.strokeStyle = "#38bdf8";
          ctx.lineWidth = Math.max(1, Math.floor(dpr));
          ctx.beginPath();
          ctx.moveTo(center.x - 10 * dpr, center.y);
          ctx.lineTo(center.x + 10 * dpr, center.y);
          ctx.moveTo(center.x, center.y - 10 * dpr);
          ctx.lineTo(center.x, center.y + 10 * dpr);
          ctx.stroke();
          ctx.restore();

          // HUD
          ctx.save();
          ctx.fillStyle = "rgba(255,255,255,0.9)";
          ctx.font = `${12 * dpr}px system-ui, -apple-system, Segoe UI, Roboto`;
          ctx.fillText(`Score: ${score}`, 12 * dpr, 20 * dpr);
          ctx.fillText(`Lives: ${lives}`, 12 * dpr, 36 * dpr);
          ctx.restore();
        },
      };

      engine.addSystem(inputSystem);
      engine.addSystem(enemySystem);
      engine.addSystem(bulletSystem);
      engine.addSystem(renderSystem);
    };

    // NEW: Creative 3D Ribbons (auto-animated ribbons with perspective and glow)
    const creative3DSample = () => {
      const canvas = engine.getCanvas();
      const ctx = engine.getContext2D();
      const size = Math.min(canvas.width, canvas.height) * 0.2;
      const center = { x: canvas.width / 2, y: canvas.height / 2 };
      const d = 4; // camera distance factor

      // Global rotation (slow orbit) to make ribbons feel 3D
      let yaw = 0;
      let pitch = 0;

      type Vec3 = { x: number; y: number; z: number };
      type Ribbon = {
        points: Vec3[];
        hue: number;
        speed: number;
        phase: number;
        wobble: number;
      };

      const ribbonCount = 8;
      const segments = 90;

      const ribbons: Ribbon[] = Array.from({ length: ribbonCount }).map((_, i) => {
        const hue = 200 + i * (160 / ribbonCount); // cool-to-warm range
        const speed = 0.8 + (i % 3) * 0.2;
        const wobble = 0.6 + (i % 5) * 0.15;
        const phase = i * 0.7;
        const points: Vec3[] = [];
        for (let s = 0; s < segments; s++) {
          const t = phase - s * 0.07;
          const x = Math.sin(t * 0.7 + i * 0.3) * 2 + Math.sin(t * 1.05 + i * 0.2) * 1.2;
          const y = Math.cos(t * 0.5 + i * 0.5) * 1.5 + Math.sin(t * 0.95 + i * 0.15) * 1.0;
          const z = -6 + Math.sin(t * 0.3 + i * 0.4) * 2;
          points.push({ x, y, z });
        }
        return { points, hue, speed, phase, wobble };
      });

      const rotateYawPitch = (x: number, y: number, z: number) => {
        // yaw around Y
        const cosy = Math.cos(yaw), siny = Math.sin(yaw);
        const x1 = x * cosy + z * siny;
        const z1 = -x * siny + z * cosy;
        // pitch around X
        const cosp = Math.cos(pitch), sinp = Math.sin(pitch);
        const y2 = y * cosp - z1 * sinp;
        const z2 = y * sinp + z1 * cosp;
        return [x1, y2, z2] as const;
      };

      const project = (x: number, y: number, z: number) => {
        const [rx, ry, rz] = rotateYawPitch(x, y, z);
        const f = d / (d + rz);
        const sx = center.x + (rx * f) * size;
        const sy = center.y + (ry * f) * size;
        return [sx, sy, rz, f] as const;
      };

      const updateSystem = {
        update(dt: number) {
          // Slow auto-rotation for the whole scene
          yaw += dt * 0.25;
          pitch += dt * 0.12;

          // Advance ribbon heads and drop tails
          for (const r of ribbons) {
            r.phase += r.speed * dt;
            const t = r.phase;
            const nx = Math.sin(t * 0.7 + r.wobble) * 2 + Math.sin(t * 1.05 + r.wobble * 0.7) * 1.2;
            const ny = Math.cos(t * 0.5 + r.wobble * 0.9) * 1.5 + Math.sin(t * 0.95 + r.wobble * 0.6) * 1.0;
            const nz = -6 + Math.sin(t * 0.3 + r.wobble * 0.8) * 2;

            r.points.unshift({ x: nx, y: ny, z: nz });
            if (r.points.length > segments) r.points.pop();
          }
        },
      };

      const renderSystem = {
        update() {
          ctx.clearRect(0, 0, canvas.width, canvas.height);

          // Background with subtle vignette
          ctx.save();
          ctx.fillStyle = "rgba(2,6,23,1)";
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          // Add a soft glow center
          const grad = ctx.createRadialGradient(center.x, center.y, 10, center.x, center.y, Math.max(canvas.width, canvas.height) * 0.6);
          grad.addColorStop(0, "rgba(56,189,248,0.07)"); // sky-400 tint
          grad.addColorStop(1, "rgba(2,6,23,0)");
          ctx.fillStyle = grad;
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.restore();

          // Grid floor lines for depth cue
          ctx.save();
          ctx.strokeStyle = "rgba(148, 163, 184, 0.12)";
          const step = 0.6;
          for (let z = -12; z <= -1; z += step) {
            const [ax, ay] = project(-3, -1.8, z);
            const [bx, by] = project(3, -1.8, z);
            ctx.beginPath();
            ctx.moveTo(ax, ay);
            ctx.lineTo(bx, by);
            ctx.stroke();
          }
          ctx.restore();

          // Draw all ribbons
          for (const r of ribbons) {
            // Build projected path
            const proj = r.points.map(p => project(p.x, p.y, p.z));
            // Average depth for fog/width
            const avgDepth = proj.reduce((acc, p) => acc + p[2], 0) / proj.length;
            const fog = Math.min(1, Math.max(0.15, 0.65 + (avgDepth + d) / (d * 5)));
            const width = Math.max(1, Math.floor(dpr * 1.2));

            // Main stroke
            ctx.save();
            ctx.lineJoin = "round";
            ctx.lineCap = "round";
            ctx.lineWidth = width;
            ctx.strokeStyle = `hsla(${Math.round(r.hue)}deg, 70%, 55%, ${fog})`;

            ctx.beginPath();
            ctx.moveTo(proj[0][0], proj[0][1]);
            for (let i = 1; i < proj.length; i++) {
              ctx.lineTo(proj[i][0], proj[i][1]);
            }
            ctx.stroke();
            ctx.restore();

            // Glow stroke (thicker, low alpha)
            ctx.save();
            ctx.lineJoin = "round";
            ctx.lineCap = "round";
            ctx.lineWidth = width * 3;
            ctx.strokeStyle = `hsla(${Math.round(r.hue)}deg, 70%, 60%, ${fog * 0.12})`;
            ctx.beginPath();
            ctx.moveTo(proj[0][0], proj[0][1]);
            for (let i = 1; i < proj.length; i++) {
              ctx.lineTo(proj[i][0], proj[i][1]);
            }
            ctx.stroke();
            ctx.restore();
          }

          // Label
          ctx.save();
          ctx.fillStyle = "rgba(255,255,255,0.9)";
          ctx.font = `${12 * dpr}px system-ui, -apple-system, Segoe UI, Roboto`;
          ctx.fillText("Creative 3D Ribbons", 12 * dpr, 20 * dpr);
          ctx.restore();
        },
      };

      engine.addSystem(updateSystem);
      engine.addSystem(renderSystem);
    };

    // ADDED: Real 3D scene using Three.js + MapleEngine canvas/input
    const webgl3DLabSample = () => {
      const canvas = engine.getCanvas();

      // Reset drawing buffer without changing visual size
      canvas.width = canvas.width;
      canvas.height = canvas.height;

      // Use Three.js to create and manage the WebGL context
      const renderer = new WebGLRenderer({ canvas, antialias: true, alpha: true });
      renderer.setPixelRatio(Math.max(1, window.devicePixelRatio || 1));
      renderer.setSize(canvas.width, canvas.height, false);

      // Scene & camera
      const scene = new Scene();
      scene.background = new Color(0x0b1220); // dark slate

      const camera = new PerspectiveCamera(60, canvas.width / canvas.height, 0.1, 100);
      camera.position.set(0, 1.4, 3);

      // Controls (mouse orbit + damping)
      const controls = new OrbitControls(camera, canvas);
      controls.enableDamping = true;
      controls.dampingFactor = 0.06;
      controls.target.set(0, 1, 0);

      // Lighting
      const ambient = new AmbientLight(0xffffff, 0.5);
      const dir = new DirectionalLight(0xffffff, 1.1);
      dir.position.set(5, 4, 2);
      scene.add(ambient, dir);

      // Floor grid + base plane
      const grid = new GridHelper(20, 40, 0x334455, 0x223344);
      grid.position.y = 0;
      scene.add(grid);

      const plane = new Mesh(
        new PlaneGeometry(20, 20),
        new MeshStandardMaterial({ color: 0x1f2937, roughness: 0.95 })
      );
      plane.rotation.x = -Math.PI / 2;
      plane.position.y = -0.001; // avoid z-fighting with grid
      scene.add(plane);

      // Orange columns to form a lab-like corridor
      const colGeom = new BoxGeometry(0.6, 1.5, 0.6);
      const colMat = new MeshStandardMaterial({ color: 0xf59e0b, roughness: 0.7, metalness: 0.05 });
      for (let gx = -4; gx <= 4; gx += 2) {
        for (let gz = -4; gz <= 4; gz += 2) {
          // leave center aisle open
          if (gx === 0 && (gz === 0 || gz === -2)) continue;
          const col = new Mesh(colGeom, colMat);
          col.position.set(gx, 0.75, gz);
          scene.add(col);
        }
      }

      // Teal capsule hero object
      const capsule = new Mesh(
        new CapsuleGeometry(0.4, 0.8, 16, 32),
        new MeshStandardMaterial({ color: 0x0ea5a5, roughness: 0.3, metalness: 0.2 })
      );
      // Compute ground resting height (half-height ≈ 0.8)
      const groundY = 0.8;
      capsule.position.set(0, groundY, 0);
      scene.add(capsule);

      // Movement state
      let yaw = 0;
      let pitch = 0;
      const pitchMin = -Math.PI / 4; // look down limit
      const pitchMax = Math.PI / 3;  // look up limit

      const moveSpeed = 2.4;      // units/sec
      const turnSpeed = 1.8;      // rad/sec (keyboard)
      const camDistance = 2.2;    // follow distance

      // Jump / gravity
      let vy = 0;
      const gravity = 9.8;        // m/s²-ish
      const jumpSpeed = 4.8;      // initial jump velocity
      let grounded = true;
      let jumpCooldown = 0;

      const capsulePos = new Vector3().copy(capsule.position);

      // Pointer lock (FPS-style mouse look)
      let pointerLocked = false;
      const onPointerLockChange = () => {
        pointerLocked = document.pointerLockElement === canvas;
        controls.enabled = !pointerLocked;
      };
      const onMouseMove = (e: MouseEvent) => {
        if (!pointerLocked) return;
        const sensitivity = 0.0025;
        yaw -= e.movementX * sensitivity;
        pitch = Math.min(pitchMax, Math.max(pitchMin, pitch - e.movementY * sensitivity));
      };
      const onCanvasClick = () => {
        canvas.requestPointerLock?.();
      };
      document.addEventListener("pointerlockchange", onPointerLockChange);
      document.addEventListener("mousemove", onMouseMove);
      canvas.addEventListener("click", onCanvasClick);

      // Keyboard movement + jump
      const inputSystem = {
        update(dt: number) {
          const input = engine.input;

          // turning (keyboard)
          const left = input.isKeyDown("ArrowLeft") || input.isKeyDown("KeyA");
          const right = input.isKeyDown("ArrowRight") || input.isKeyDown("KeyD");
          if (!pointerLocked) { // when mouse is locked, mouse controls yaw/pitch
            if (left) yaw += turnSpeed * dt;
            if (right) yaw -= turnSpeed * dt;
          }

          // forward/back
          const forward = input.isKeyDown("ArrowUp") || input.isKeyDown("KeyW");
          const backward = input.isKeyDown("ArrowDown") || input.isKeyDown("KeyS");
          const dirX = -Math.sin(yaw);
          const dirZ = -Math.cos(yaw);
          if (forward) {
            capsulePos.x += dirX * moveSpeed * dt;
            capsulePos.z += dirZ * moveSpeed * dt;
          }
          if (backward) {
            capsulePos.x -= dirX * moveSpeed * dt;
            capsulePos.z -= dirZ * moveSpeed * dt;
          }

          // Jump
          jumpCooldown = Math.max(0, jumpCooldown - dt);
          if ((input.isKeyDown("Space") || input.isKeyDown("Numpad0")) && grounded && jumpCooldown <= 0) {
            vy = jumpSpeed;
            grounded = false;
            jumpCooldown = 0.18; // tiny debounce so holding space doesn't multi-trigger
          }

          // Apply horizontal pose
          capsule.position.copy(capsulePos);
          capsule.rotation.y = yaw;

          // Camera follow (third-person, honors pitch)
          const camOffset = new Vector3(-Math.sin(yaw), Math.sin(pitch) * 0.6, -Math.cos(yaw)).multiplyScalar(camDistance);
          camera.position.set(capsulePos.x + camOffset.x, capsulePos.y + 1.0 + camOffset.y, capsulePos.z + camOffset.z);
          const lookTargetY = capsulePos.y + 0.6; // look at upper-middle of capsule
          if (pointerLocked) {
            camera.lookAt(capsulePos.x, lookTargetY, capsulePos.z);
          } else {
            controls.target.set(capsulePos.x, lookTargetY, capsulePos.z);
            controls.update();
          }
        },
      };

      // Simple physics for vertical motion
      const physicsSystem = {
        update(dt: number) {
          vy -= gravity * dt;
          capsulePos.y += vy * dt;

          if (capsulePos.y <= groundY) {
            capsulePos.y = groundY;
            vy = 0;
            grounded = true;
          }
        },
      };

      // Resize when MapleEngine resizes the canvas
      let prevW = canvas.width, prevH = canvas.height;
      const resizeSystem = {
        update() {
          if (canvas.width !== prevW || canvas.height !== prevH) {
            renderer.setSize(canvas.width, canvas.height, false);
            camera.aspect = canvas.width / canvas.height;
            camera.updateProjectionMatrix();
            prevW = canvas.width; prevH = canvas.height;
          }
        },
      };

      const renderSystem = {
        update() {
          renderer.render(scene, camera);
        },
      };

      engine.addSystem(inputSystem);
      engine.addSystem(physicsSystem);
      engine.addSystem(resizeSystem);
      engine.addSystem(renderSystem);
    };

    // ADDED: PBR Environment sample
    const pbrEnvironmentSample = () => {
      const canvas = engine.getCanvas();

      // WORKAROUND: some environments (Electron/webview) throw InvalidStateError on setPointerCapture.
      const el = canvas as unknown as { setPointerCapture?: (id: number) => void; style?: any };
      if (el && typeof el.setPointerCapture === "function") {
        el.setPointerCapture = (_id: number) => {};
      }
      // Respect interactivity setting: disable pointer events when not interactive
      (canvas as HTMLCanvasElement).style.touchAction = "none";
      (canvas as HTMLCanvasElement).style.pointerEvents = envInteractiveRef.current ? "auto" : "none";

      // Reset drawing buffer without changing visual size
      canvas.width = canvas.width;
      canvas.height = canvas.height;

      // Use Three.js to create and manage the WebGL context
      const renderer = new WebGLRenderer({ canvas, antialias: true, alpha: true });
      renderer.setPixelRatio(Math.max(1, window.devicePixelRatio || 1));
      renderer.setSize(canvas.width, canvas.height, false);

      const scene = new Scene();
      scene.background = new Color(0x0b1220);

      const camera = new PerspectiveCamera(55, canvas.width / canvas.height, 0.1, 200);
      camera.position.set(0, 1.4, 3);

      // REPLACED: OrbitControls -> TrackballControls to avoid pointer capture error
      const controls = new TrackballControls(camera, canvas);
      controls.rotateSpeed = 3.0;
      controls.zoomSpeed = 1.2;
      controls.panSpeed = 0.8;
      controls.noPan = true;
      controls.staticMoving = false;
      controls.dynamicDampingFactor = 0.15;
      controls.target.set(0, 1.0, 0);
      // NEW: enable/disable controls based on setting
      controls.enabled = envInteractiveRef.current;

      // Lighting
      const ambient = new AmbientLight(0xffffff, 0.25);
      const dir = new DirectionalLight(0xffffff, 0.6);
      dir.position.set(3, 5, 2);
      scene.add(ambient, dir);

      // Floor grid reference (subtle)
      const grid = new GridHelper(40, 80, 0x334455, 0x223344);
      grid.position.y = 0;
      scene.add(grid);

      // PMREM for HDR env
      const pmrem = new PMREMGenerator(renderer);
      pmrem.compileEquirectangularShader();

      // REPLACED: GroundProjectedSkybox with a rotatable HDR sky dome
      let skyDome: Mesh | null = null;

      // Simple pedestal + PBR objects
      const pedestal = new Mesh(
        new BoxGeometry(1.4, 0.3, 1.0),
        new MeshStandardMaterial({ color: 0x8b5a2b, roughness: 0.7, metalness: 0.1 })
      );
      pedestal.position.set(0, 0.15, 0);
      scene.add(pedestal);

      const metalBase = new MeshPhysicalMaterial({
        metalness: 1.0,
        roughness: 0.15,
        clearcoat: 1.0,
        clearcoatRoughness: 0.12
      });

      const sphere = new Mesh(new SphereGeometry(0.45, 64, 64), metalBase.clone());
      sphere.position.set(-0.55, 0.75, 0);

      const knot = new Mesh(new TorusKnotGeometry(0.35, 0.12, 200, 32), metalBase.clone());
      knot.position.set(0.55, 0.82, 0);

      scene.add(sphere, knot);

      // HDR presets (Poly Haven 1k EXR files)
      const envURLs: Record<string, string> = {
        cliff:   "https://dl.polyhaven.org/file/ph-assets/HDRIs/exr/1k/harties_1k.exr",
        room:    "https://dl.polyhaven.org/file/ph-assets/HDRIs/exr/1k/studio_small_03_1k.exr",
        parking: "https://dl.polyhaven.org/file/ph-assets/HDRIs/exr/1k/abandoned_parking_1k.exr",
        country: "https://dl.polyhaven.org/file/ph-assets/HDRIs/exr/1k/country_kitchen_1k.exr"
      };

      let currentEnvName: string | null = null;
      let loading = false;

      const loader = new EXRLoader();

      const applyEnvironment = (envTexture: Texture) => {
        // Prepare env map for background and reflections
        envTexture.mapping = EquirectangularReflectionMapping;

        // PMREM for PBR env reflections
        const envRT = pmrem.fromEquirectangular(envTexture);

        // Set scene.environment for PBR reflections
        scene.environment = envRT.texture;
        // Render background via a sky dome mesh (for easy rotation)
        scene.background = null;

        // Create or replace the sky dome
        if (skyDome) {
          scene.remove(skyDome);
          skyDome.geometry.dispose();
          (skyDome.material as MeshBasicMaterial).map?.dispose?.();
          (skyDome.material as MeshBasicMaterial).dispose();
          skyDome = null;
        }

        skyDome = new Mesh(
          new SphereGeometry(60, 64, 64),
          new MeshBasicMaterial({ map: envTexture, side: BackSide })
        );
        skyDome.rotation.order = "YXZ";
        scene.add(skyDome);
      };

      const loadEnvironment = async (name: string) => {
        if (loading || name === currentEnvName) return;
        const url = envURLs[name] ?? envURLs["room"];
        loading = true;
        try {
          const tex = await loader.loadAsync(url);
          applyEnvironment(tex);
          currentEnvName = name;
        } finally {
          loading = false;
        }
      };

      // kick off initial env
      loadEnvironment(envPresetRef.current);

      // Live controls system
      let lastRot = envRotationRef.current;

      const controlsSystem = {
        update(dt: number) {
          // auto-rotate env
          if (envAutoRotateRef.current) {
            envRotationRef.current += dt * 0.2;
          }
          const TWO_PI = Math.PI * 2;
          if (envRotationRef.current > TWO_PI) envRotationRef.current -= TWO_PI;
          if (envRotationRef.current < -TWO_PI) envRotationRef.current += TWO_PI;

          if (skyDome) {
            skyDome.rotation.y = envRotationRef.current;
          }

          if (envPresetRef.current !== currentEnvName) {
            loadEnvironment(envPresetRef.current);
          }

          if (Math.abs(envRotationRef.current - lastRot) > 0.0001) {
            const delta = envRotationRef.current - lastRot;
            sphere.rotation.y += delta * 0.5;
            knot.rotation.y += delta * 0.5;
            lastRot = envRotationRef.current;
          }

          // NEW: keep canvas events and controls aligned with setting
          const desiredPointer = envInteractiveRef.current ? "auto" : "none";
          if ((canvas as HTMLCanvasElement).style.pointerEvents !== desiredPointer) {
            (canvas as HTMLCanvasElement).style.pointerEvents = desiredPointer;
          }
          controls.enabled = envInteractiveRef.current;

          if (controls.enabled) {
            controls.update();
          }
        },
      };

      // Resize handling
      let prevW = canvas.width, prevH = canvas.height;
      const resizeSystem = {
        update() {
          if (canvas.width !== prevW || canvas.height !== prevH) {
            renderer.setSize(canvas.width, canvas.height, false);
            camera.aspect = canvas.width / canvas.height;
            camera.updateProjectionMatrix();
            prevW = canvas.width; prevH = canvas.height;
          }
        },
      };

      const renderSystem = {
        update() {
          renderer.render(scene, camera);
        },
      };

      engine.addSystem(controlsSystem);
      engine.addSystem(resizeSystem);
      engine.addSystem(renderSystem);
    };

    // Launch selected sample
    engine.addSystem(statsSystem);
    if (selected === "moving-dot") {
      dotSample();
    } else if (selected === "bouncing-balls") {
      bouncingBallsSample();
    } else if (selected === "spinning-cube") {
      spinningCubeSample();
    } else if (selected === "shaded-icosahedron") {
      shadedIcosahedronSample();
    } else if (selected === "shooter-3d") {
      shooter3DSample();
    } else if (selected === "creative-3d") {
      creative3DSample();
    } else if (selected === "webgl-3d-lab") {
      webgl3DLabSample();
    } else if (selected === "pbr-environment") {
      pbrEnvironmentSample();
    }
  }, [selected, dpr]);

  useEffect(() => {
    toast({
      title: "Launching sample",
      description: samples.find(s => s.id === selected)?.label,
    });
  }, [selected, toast]);

  return (
    <div className="h-full w-full p-2 md:p-3">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 h-full">
        {/* Left: Environment & Controls */}
        <Card className="p-3 flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Box className="w-4 h-4 text-muted-foreground" />
              <h3 className="text-sm font-medium">MapleEngine</h3>
            </div>
            <Badge variant="outline">v1</Badge>
          </div>
          <Separator />
          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Activity className="w-4 h-4" />
                <span>FPS (measured)</span>
              </div>
              <span className="font-medium">{fps}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Gauge className="w-4 h-4" />
                <span>Target FPS</span>
              </div>
              <span className="font-medium">{targetFPS}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Monitor className="w-4 h-4" />
                <span>Canvas (px)</span>
              </div>
              <span className="font-medium">{canvasPx.w} × {canvasPx.h}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Monitor className="w-4 h-4" />
                <span>Canvas (css)</span>
              </div>
              <span className="font-medium">{cssSize.w} × {cssSize.h}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Cpu className="w-4 h-4" />
                <span>DPR</span>
              </div>
              <span className="font-medium">{dpr}</span>
            </div>
          </div>

          <Separator className="my-2" />

          {/* Sample selector */}
          <div className="space-y-2">
            <label className="text-xs text-muted-foreground">Sample</label>
            <Select value={selected} onValueChange={(v) => setSelected(v as SampleId)}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select a sample" />
              </SelectTrigger>
              <SelectContent>
                {samples.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button className="w-full" onClick={() => toast({ title: "Sample running", description: samples.find(s => s.id === selected)?.label })}>
              <Play className="w-4 h-4 mr-2" />
              Launch
            </Button>
          </div>

          {/* Sample-specific controls */}
          {selected === "pbr-environment" && (
            <div className="space-y-3">
              <Separator className="my-1" />
              <div className="text-xs font-medium text-muted-foreground">Environment</div>

              <div className="flex items-center justify-between">
                <span className="text-sm">Auto Rotate</span>
                <Switch checked={envAutoRotate} onCheckedChange={setEnvAutoRotate} />
              </div>

              {/* NEW: Interactivity toggle */}
              <div className="flex items-center justify-between">
                <span className="text-sm">Interactive (mouse/touch)</span>
                <Switch checked={envInteractive} onCheckedChange={setEnvInteractive} />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span>Rotation</span>
                  <span className="font-mono">{envRotationDeg.toFixed(1)}°</span>
                </div>
                <Slider
                  value={[envRotationDeg]}
                  max={360}
                  step={0.1}
                  onValueChange={(v) => setEnvRotationDeg(v[0] ?? 0)}
                />
              </div>

              <div className="space-y-2">
                <label className="text-xs text-muted-foreground">Preset</label>
                <Select value={envPreset} onValueChange={(v) => setEnvPreset(v as any)}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select environment" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cliff">Harties Cliff</SelectItem>
                    <SelectItem value="room">Room</SelectItem>
                    <SelectItem value="parking">Parking Lot</SelectItem>
                    <SelectItem value="country">Country</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span>Skybox Ground Height</span>
                  <span className="font-mono">{groundHeight.toFixed(2)}</span>
                </div>
                <Slider
                  value={[groundHeight]}
                  min={0}
                  max={5}
                  step={0.01}
                  onValueChange={(v) => setGroundHeight(v[0] ?? 0)}
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span>Skybox Ground Radius</span>
                  <span className="font-mono">{groundRadius.toFixed(2)}</span>
                </div>
                <Slider
                  value={[groundRadius]}
                  min={1}
                  max={20}
                  step={0.01}
                  onValueChange={(v) => setGroundRadius(v[0] ?? 1)}
                />
              </div>
            </div>
          )}

          <Separator className="my-2" />

          {/* Notes */}
          <ScrollArea className="h-24">
            <div className="text-xs text-muted-foreground space-y-2 pr-2">
              <p>MapleEngine uses a fixed timestep for stable physics and responsive input.</p>
              <p>Resize the window to see canvas stats update. Try changing DPR (browser zoom) to test scaling.</p>
              <p>Use WASD/Arrows to control the Moving Dot sample.</p>
            </div>
          </ScrollArea>
        </Card>

        {/* Right: Preview */}
        <Card className="p-0 md:col-span-2 overflow-hidden">
          <div className="bg-muted/30 border-b px-3 py-2 flex items-center justify-between">
            <div className="text-sm text-muted-foreground">Preview</div>
            <div className="flex items-center gap-2">
              <Badge variant="outline">{samples.find(s => s.id === selected)?.label}</Badge>
              <Button variant="ghost" size="sm" onClick={toggleFullscreen} title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}>
                {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
              </Button>
            </div>
          </div>
          <div ref={viewportRef} className="h-[320px] md:h-[420px] w-full">
            <MapleGameHost
              appId="maple-engine"
              targetFPS={targetFPS}
              createGame={createGame}
              className="h-full w-full"
              renderMode={selected === "webgl-3d-lab" || selected === "pbr-environment" ? "webgl" : "2d"}
            />
          </div>
        </Card>
      </div>
    </div>
  );
};

export default MapleEngineApp;